import * as THREE from '@phdsoft/three';
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

/**
 * Renders a known set of CSS2D labels instead of the whole scene.
 *
 * CSS2DRenderer.render(scene, camera) calls scene.updateMatrixWorld() and walks every
 * object in the scene (twice with sortObjects on) to find its CSS2DObjects, every frame.
 * The labels are few; the scene holds thousands of meshes. Label creators register their
 * labels here and renderRegisteredCss2dLabels positions only those, with the same
 * visibility, transform and z-order rules as three's CSS2DRenderer.
 * A CSS2DObject that is not registered here is not drawn, even when it is in the scene.
 */

const registeredLabels = new Set<CSS2DObject>();

const _vector = new THREE.Vector3();
const _viewProjectionMatrix = new THREE.Matrix4();
const _cameraPosition = new THREE.Vector3();
const _labelPosition = new THREE.Vector3();

// Labels that already carry the 'added' / 'removed' listeners, so re-registering never adds them twice.
const labelsWithListeners = new WeakSet<CSS2DObject>();

function onLabelAdded(event: { target: CSS2DObject }): void {
    registerCss2dLabel(event.target);
}

function onLabelRemoved(event: { target: CSS2DObject }): void {
    unregisterCss2dLabel(event.target);
}

/**
 * Registers the label, and keeps it registered while it is parented: its own 'removed'
 * event (e.g. ThreeJsCleanup.disposeObject) unregisters it, its own 'added' event registers
 * it again. Removing an ancestor group fires neither, so toggled label groups stay registered.
 */
export function registerCss2dLabel(label: CSS2DObject): void {
    registeredLabels.add(label);
    if (!labelsWithListeners.has(label)) {
        labelsWithListeners.add(label);
        label.addEventListener('added', onLabelAdded);
        label.addEventListener('removed', onLabelRemoved);
    }
}

export function unregisterCss2dLabel(label: CSS2DObject): void {
    registeredLabels.delete(label);
}

// was: /** Tests only. */
/**
 * Forgets every registered label. Graphics.destroy() calls it at digital-twin teardown, so
 * labels removed with their group but never disposed (e.g. anomaly labels toggled off) do
 * not stay registered into the next session. Specs call it between tests.
 */
export function clearCss2dLabelRegistry(): void {
    registeredLabels.clear();
}

/** Tests only. */
export function css2dLabelRegistrySize(): number {
    return registeredLabels.size;
}

/** True when the label and all its ancestors are visible and its root is `scene`. */
function isAttachedAndVisible(label: THREE.Object3D, scene: THREE.Object3D): boolean {
    let object: THREE.Object3D = label;
    while (object.parent !== null) {
        if (object.visible === false) return false;
        object = object.parent;
    }
    return object === scene && object.visible !== false;
}

export function renderRegisteredCss2dLabels(renderer: CSS2DRenderer, scene: THREE.Object3D, camera: THREE.Camera): void {
    // Same camera update as CSS2DRenderer.render; the scene itself is never updated or walked.
    if (camera.parent === null && camera.matrixWorldAutoUpdate === true) camera.updateMatrixWorld();
    _viewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _cameraPosition.setFromMatrixPosition(camera.matrixWorld);

    const { width, height } = renderer.getSize();
    const widthHalf = width / 2;
    const heightHalf = height / 2;

    const shown: { label: CSS2DObject; distanceToCameraSquared: number }[] = [];

    for (const label of registeredLabels) {
        const element = label.element;
        // Detached or hidden. CSS2DObject only removes its element on its own 'removed'
        // event, so removing an ancestor group would otherwise leave the label on screen.
        if (!isAttachedAndVisible(label, scene)) {
            element.style.display = 'none';
            continue;
        }

        label.updateWorldMatrix(true, false);
        _vector.setFromMatrixPosition(label.matrixWorld);
        _vector.applyMatrix4(_viewProjectionMatrix);

        const visible = (_vector.z >= -1 && _vector.z <= 1) && (label.layers.test(camera.layers) === true);
        element.style.display = visible === true ? '' : 'none';
        if (visible !== true) continue;

        label.onBeforeRender(renderer, scene as THREE.Scene, camera);

        element.style.transform = 'translate(' + (-100 * label.center.x) + '%,' + (-100 * label.center.y) + '%)'
            + 'translate(' + (_vector.x * widthHalf + widthHalf) + 'px,' + (-_vector.y * heightHalf + heightHalf) + 'px)';

        if (element.parentNode !== renderer.domElement) {
            renderer.domElement.appendChild(element);
        }

        label.onAfterRender(renderer, scene as THREE.Scene, camera);

        _labelPosition.setFromMatrixPosition(label.matrixWorld);
        shown.push({ label, distanceToCameraSquared: _cameraPosition.distanceToSquared(_labelPosition) });
    }

    if (renderer.sortObjects) {
        // CSS2DRenderer's zOrder: higher renderOrder first, then nearer to the camera first.
        shown.sort((a, b) => {
            if (a.label.renderOrder !== b.label.renderOrder) {
                return b.label.renderOrder - a.label.renderOrder;
            }
            return a.distanceToCameraSquared - b.distanceToCameraSquared;
        });

        const zMax = shown.length;
        for (let i = 0; i < zMax; i++) {
            shown[i].label.element.style.zIndex = String(zMax - i);
        }
    }
}
